/**
 * Records video with the phone's camera inside PRISM, at a chosen resolution.
 * (Safari's own "Record video" from a web page records at iOS's medium web
 * quality, about 480p, and a page cannot change that.) Uses getUserMedia and
 * MediaRecorder, which Safari supports from iOS 14.3; needs HTTPS. The
 * recording is reviewed, then uploaded like any file.
 *
 * 1.6.4: the picture fills the screen and the controls float over it. In landscape they form a column on the
 * right (as in the Camera app), clear of the Dynamic Island on the left; in portrait they sit at the bottom.
 * Resolution and frame rate live in a small settings sheet behind a chip. Turning the phone while not recording
 * reopens the camera for the new orientation; a take keeps its stream. Zoom and torch show where the camera has
 * them, and the screen stays awake while the recorder is open.
 */
import { useEffect, useRef, useState } from 'react';
import { Circle, Flashlight, FlashlightOff, RotateCcw, SwitchCamera, Square, Upload, X } from 'lucide-react';
import { safeStorage } from '../lib/prefs';
import { holdWakeLock } from '../lib/wake-lock';

const QUALITIES = {
  '720p': { width: 1280, height: 720, bitrate: 6_000_000 },
  '1080p': { width: 1920, height: 1080, bitrate: 12_000_000 },
  '2160p': { width: 3840, height: 2160, bitrate: 35_000_000 },
} as const;
type Quality = keyof typeof QUALITIES;
/** 25 fps by default (European broadcast); 30 where that is the house rate. */
const RATES = [25, 30] as const;
type Rate = (typeof RATES)[number];
const QUALITY_LABEL: Record<Quality, string> = { '720p': '720p', '1080p': '1080p', '2160p': '4K' };
/** Zoom steps offered where the camera's range covers them. */
const ZOOM_STEPS = [0.5, 1, 2, 3, 5];
type CameraCaps = MediaTrackCapabilities & { zoom?: { min: number; max: number }; torch?: boolean };
type CameraSettings = MediaTrackSettings & { zoom?: number; torch?: boolean };

const landscapeQuery = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(orientation: landscape)') : null);

/** MP4 (H.264) where the browser can record it, else WebM. */
function pickType(): { mime: string; ext: string } {
  const candidates = [
    { mime: 'video/mp4;codecs=avc1.640028,mp4a.40.2', ext: 'mp4' },
    { mime: 'video/mp4', ext: 'mp4' },
    { mime: 'video/webm;codecs=vp9,opus', ext: 'webm' },
    { mime: 'video/webm', ext: 'webm' },
  ];
  return candidates.find((c) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c.mime)) ?? { mime: '', ext: 'mp4' };
}

export const canRecordInPage = () => typeof window !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== 'undefined' && window.isSecureContext;

export function Recorder({ onDone, onClose }: { onDone: (file: File) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const [quality, setQuality] = useState<Quality>(() => {
    const q = safeStorage.getItem('prism.recorder.quality');
    return q && q in QUALITIES ? (q as Quality) : '1080p';
  });
  const [rate, setRate] = useState<Rate>(() => (Number(safeStorage.getItem('prism.recorder.rate')) === 30 ? 30 : 25));
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const [state, setState] = useState<'starting' | 'ready' | 'recording' | 'review' | 'error'>('starting');
  const [error, setError] = useState<string | null>(null);
  const [actual, setActual] = useState<{ width: number; height: number; fps?: number } | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [result, setResult] = useState<{ file: File; url: string } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The phone's orientation now, and the one the camera was opened for (it follows only while not recording).
  const [landscape, setLandscape] = useState(() => landscapeQuery()?.matches ?? false);
  const [openLandscape, setOpenLandscape] = useState(landscape);
  const [opens, setOpens] = useState(0);
  const [zoomRange, setZoomRange] = useState<{ min: number; max: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [hasTorch, setHasTorch] = useState(false);
  const [torch, setTorch] = useState(false);
  // 1.6.4: the camera is given up whenever the page is hidden (another app, the lock screen, a suspended Home Screen
  // app), so iOS never resumes it on return without the recorder in view; it reopens when the page is shown again.
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const idle = state !== 'recording' && state !== 'review';

  useEffect(() => {
    const mq = landscapeQuery();
    if (!mq) return;
    const on = () => setLandscape(mq.matches);
    mq.addEventListener?.('change', on);
    window.addEventListener('resize', on);
    return () => { mq.removeEventListener?.('change', on); window.removeEventListener('resize', on); };
  }, []);
  // Not recording: a turned phone reopens the camera for the new orientation. Recording: the take keeps its stream.
  useEffect(() => { if (idle) setOpenLandscape(landscape); }, [idle, landscape]);
  useEffect(() => holdWakeLock(), []);

  /** Stops every track of the open stream and forgets it. */
  function releaseCamera() {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (video.current) video.current.srcObject = null;
  }
  // Hidden: a take ends (and goes to review as usual), then the camera is released. Shown: the open effect reopens it.
  useEffect(() => {
    const hide = () => {
      if (recorder.current?.state === 'recording') recorder.current.stop(); // onstop releases the tracks too
      releaseCamera();
      setVisible(false);
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') hide(); else setVisible(true); };
    const onShow = () => { if (document.visibilityState !== 'hidden') setVisible(true); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', onShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', onShow);
    };
  }, []);

  // (Re)open the camera for the chosen quality, side and orientation.
  useEffect(() => {
    if (state === 'review') return;
    // The stream in use goes before a new one is asked for (a turned phone, another setting, the other camera), so
    // two never run at once; hidden, nothing is opened until the page is shown again.
    releaseCamera();
    if (!visible) { setState((st) => (st === 'recording' || st === 'review' ? st : 'starting')); return; }
    let cancelled = false;
    const q = QUALITIES[quality];
    // Landscape asks for a landscape picture, portrait for a portrait one.
    const [width, height] = openLandscape ? [q.width, q.height] : [q.height, q.width];
    setState('starting');
    // Ask the camera itself for the rate: exactly, then capped, then as a preference. No conversion:
    // a camera that will not run at it records at its own rate, and the settings say which.
    const open = (frameRate: MediaTrackConstraints['frameRate']) => navigator.mediaDevices.getUserMedia({
      video: { facingMode: facing, width: { ideal: width }, height: { ideal: height }, frameRate },
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true },
    });
    const overconstrained = (next: () => Promise<MediaStream>) => (err: Error) => { if (err.name !== 'OverconstrainedError') throw err; return next(); };
    open({ exact: rate })
      .catch(overconstrained(() => open({ ideal: rate, max: rate })))
      .catch(overconstrained(() => open({ ideal: rate })))
      .then(async (s) => {
      if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
      const track = s.getVideoTracks()[0];
      if (track && Math.round(track.getSettings().frameRate ?? rate) !== rate) {
        await track.applyConstraints({ frameRate: { exact: rate } })
          .catch(() => track.applyConstraints({ frameRate: { min: rate, max: rate } }))
          .catch(() => undefined);
      }
      if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
      releaseCamera();
      stream.current = s;
      const settings = track?.getSettings() as CameraSettings | undefined;
      setActual(settings?.width && settings.height ? { width: settings.width, height: settings.height, fps: settings.frameRate ? Math.round(settings.frameRate) : undefined } : null);
      // Zoom and torch only where the camera offers them.
      const caps = (typeof track?.getCapabilities === 'function' ? track.getCapabilities() : {}) as CameraCaps;
      setZoomRange(caps.zoom && caps.zoom.max > caps.zoom.min ? { min: caps.zoom.min, max: caps.zoom.max } : null);
      setZoom(settings?.zoom ?? 1);
      setHasTorch(caps.torch === true);
      setTorch(false);
      setError(null);
      if (video.current) { video.current.srcObject = s; void video.current.play().catch(() => undefined); }
      setOpens((n) => n + 1);
      setState('ready');
    }).catch((err: Error) => {
      if (cancelled) return;
      setError(err.name === 'NotAllowedError' ? 'PRISM may not use the camera. Allow camera and microphone for this site in the browser settings.' : `The camera could not be opened: ${err.message}`);
      setState('error');
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quality, rate, facing, openLandscape, state === 'review', visible]);

  // Leaving the recorder by any route (close, Upload, a parent that unmounts it) releases the camera.
  useEffect(() => () => releaseCamera(), []);
  useEffect(() => () => { if (result) URL.revokeObjectURL(result.url); }, [result]);

  useEffect(() => {
    if (state !== 'recording') return;
    const start = Date.now();
    const t = window.setInterval(() => setSeconds(Math.floor((Date.now() - start) / 1000)), 500);
    return () => window.clearInterval(t);
  }, [state]);

  function start() {
    if (!stream.current) return;
    setSettingsOpen(false);
    const type = pickType();
    chunks.current = [];
    const r = new MediaRecorder(stream.current, { ...(type.mime ? { mimeType: type.mime } : {}), videoBitsPerSecond: QUALITIES[quality].bitrate, audioBitsPerSecond: 192_000 });
    r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    r.onstop = () => {
      const blob = new Blob(chunks.current, { type: r.mimeType || type.mime || 'video/mp4' });
      const ext = (r.mimeType || type.mime).includes('webm') ? 'webm' : 'mp4';
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const file = new File([blob], `recording-${stamp}.${ext}`, { type: blob.type });
      releaseCamera();
      setResult({ file, url: URL.createObjectURL(blob) });
      setState('review');
    };
    // Chunks every second keep memory steady and lose little if the page is closed.
    r.start(1000);
    recorder.current = r;
    setSeconds(0);
    setState('recording');
  }

  function again() {
    if (result) URL.revokeObjectURL(result.url);
    setResult(null);
    setOpenLandscape(landscape);
    setState('starting');
  }

  function applyZoom(z: number) {
    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    setZoom(z);
    void track.applyConstraints({ advanced: [{ zoom: z } as MediaTrackConstraintSet] }).catch(() => undefined);
  }

  function toggleTorch() {
    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torch;
    setTorch(next);
    void track.applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] }).catch(() => setTorch(!next));
  }

  const time = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  const review = state === 'review' && result;
  const zoomSteps = zoomRange ? ZOOM_STEPS.filter((z) => z >= zoomRange.min - 0.01 && z <= zoomRange.max + 0.01) : [];
  const actualLabel = actual ? `${Math.max(actual.width, actual.height)}×${Math.min(actual.width, actual.height)}${actual.fps ? ` · ${actual.fps} fps` : ''}` : '';
  const turned = state === 'recording' && landscape !== openLandscape;

  const close = <button className="m-rec-round m-rec-close" onClick={() => { if (recorder.current?.state === 'recording') recorder.current.stop(); releaseCamera(); onClose(); }} aria-label="Close" data-testid="m-rec-close"><X size={22} /></button>;
  const switchCamera = idle && (
    <button className="m-rec-round" onClick={() => setFacing(facing === 'environment' ? 'user' : 'environment')} aria-label="Switch camera" data-testid="m-rec-switch"><SwitchCamera size={22} /></button>
  );
  const torchButton = hasTorch && !review && (
    <button className={`m-rec-round ${torch ? 'on' : ''}`} onClick={toggleTorch} aria-label={torch ? 'Torch off' : 'Torch on'} aria-pressed={torch} data-testid="m-rec-torch">{torch ? <Flashlight size={20} /> : <FlashlightOff size={20} />}</button>
  );
  const chip = !review && (
    <button className="m-rec-chip" disabled={state === 'recording'} onClick={() => setSettingsOpen((o) => !o)} aria-label="Recording settings" aria-expanded={settingsOpen} data-testid="m-rec-settings">
      {QUALITY_LABEL[quality]} · {rate}
    </button>
  );
  const timer = state === 'recording' && <span className="m-rec-time" data-testid="m-rec-time"><Circle size={10} fill="currentColor" /> {time}</span>;
  const recordButton = state === 'recording' ? (
    <button className="m-rec-button stop" onClick={() => recorder.current?.stop()} aria-label="Stop recording" data-testid="m-rec-stop"><Square size={landscape ? 22 : 26} fill="currentColor" /></button>
  ) : (
    <button className="m-rec-button" disabled={state !== 'ready'} onClick={start} aria-label="Start recording" data-testid="m-rec-start"><Circle size={landscape ? 46 : 56} fill="currentColor" /></button>
  );
  const megabytes = result ? (result.file.size / 1e6).toFixed(0) : '0';
  const againButton = <button className="m-rec-action" onClick={again} data-testid="m-rec-again"><RotateCcw size={20} /><span>Record again</span></button>;
  const uploadButton = <button className="m-rec-action primary" onClick={() => { releaseCamera(); if (result) onDone(result.file); }} data-testid="m-rec-upload"><Upload size={20} /><span>Upload {megabytes} MB</span></button>;
  const zoomChips = zoomSteps.length > 1 && !review && (
    <div className="m-rec-zoom" role="group" aria-label="Zoom" data-testid="m-rec-zoom">
      {zoomSteps.map((z) => <button key={z} className={Math.abs(zoom - z) < 0.05 ? 'on' : ''} onClick={() => applyZoom(z)} aria-pressed={Math.abs(zoom - z) < 0.05}>{z}×</button>)}
    </div>
  );

  return (
    <div className={`m-recorder ${landscape ? 'landscape' : 'portrait'} ${review ? 'reviewing' : ''}`} data-testid="m-recorder" data-state={state} data-opens={opens}
      data-stream={actual ? `${actual.width}x${actual.height}` : ''} data-orientation={landscape ? 'landscape' : 'portrait'}>
      {review ? (
        <video src={result.url} controls playsInline className="m-recorder-view" data-testid="m-recorded" />
      ) : (
        <video ref={video} muted playsInline autoPlay className={`m-recorder-view ${facing === 'user' ? 'mirror' : ''}`} data-testid="m-rec-view" />
      )}
      {landscape ? (
        <>
          {close}
          <div className="m-rec-column" data-testid="m-rec-controls">
            <div className="m-rec-slot">{review ? againButton : <>{switchCamera}{torchButton}</>}</div>
            <div className="m-rec-slot mid">{review ? uploadButton : <>{timer}{recordButton}</>}</div>
            <div className="m-rec-slot">{chip}</div>
          </div>
        </>
      ) : (
        <>
          <div className="m-rec-top">
            {close}
            <span className="m-rec-top-right">{torchButton}{chip}</span>
          </div>
          <div className="m-rec-bottom" data-testid="m-rec-controls">
            {review ? <div className="m-rec-row">{againButton}{uploadButton}</div> : (
              <>
                {timer}
                <div className="m-rec-row"><span className="m-rec-spacer" />{recordButton}<span className="m-rec-spacer">{switchCamera}</span></div>
              </>
            )}
          </div>
        </>
      )}
      {zoomChips}
      {turned && <div className="m-rec-note" data-testid="m-rec-orientation-note">Recording in {openLandscape ? 'landscape' : 'portrait'}</div>}
      {error && <div className="m-rec-error">{error}</div>}
      {settingsOpen && !review && (
        <>
          <div className="m-rec-sheet-backdrop" onClick={() => setSettingsOpen(false)} />
          <div className="m-rec-sheet" role="dialog" aria-label="Recording settings" data-testid="m-rec-sheet">
            <label><span>Resolution</span>
              <select value={quality} onChange={(e) => { setQuality(e.target.value as Quality); safeStorage.setItem('prism.recorder.quality', e.target.value); }} aria-label="Resolution" data-testid="m-rec-quality">
                <option value="720p">720p</option><option value="1080p">1080p</option><option value="2160p">4K</option>
              </select>
            </label>
            <label><span>Frame rate</span>
              <select value={rate} onChange={(e) => { const r = Number(e.target.value) as Rate; setRate(r); safeStorage.setItem('prism.recorder.rate', String(r)); }} aria-label="Frame rate" data-testid="m-rec-rate">
                {RATES.map((r) => <option key={r} value={r}>{r} fps</option>)}
              </select>
            </label>
            <p className="m-rec-actual" data-testid="m-rec-actual">{actualLabel ? `Camera: ${actualLabel}` : state === 'starting' ? 'Opening the camera…' : 'Camera size unknown'}</p>
          </div>
        </>
      )}
    </div>
  );
}
